import * as React from "react";
import { modelKeyToDealTextMap, a11yKeyToTextMap, buildDatesLabel } from "../checkPricesLocalization";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { DealProps } from "../typings";
import {
  UitkCardTextSegment,
  UitkCardSectionSeparatorType,
  UitkCardContent,
  UitkCard,
  UitkCardLink,
} from "uitk-react-cards";
import { viewContext } from "../CheckPrices";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";

export const Deal: React.FC<DealProps> = ({ modelKey, model, context, l10n }) => {
  if (!modelKeyToDealTextMap[modelKey] || !model[modelKey]) {
    return null;
  }

  const { searchContext } = context;

  const content = model[modelKey];
  const { formatText } = useLocalization();
  const { name, type } = searchContext?.location;
  const datesLabel = buildDatesLabel(l10n, content.isoStartDate, content.isoEndDate);
  const data = model.dataType === "dates" ? datesLabel : content.price || formatText("checkPrices.label.getPrice");

  const cardLink = getCardLink(modelKey, content.link, context);

  const props = {
    a11yText: a11yKeyToTextMap[modelKey]({ regionName: name, datesLabel, regionType: type, l10n }),
    cardLink,
    label: modelKeyToDealTextMap[modelKey](l10n),
    data,
    modelKey,
  };

  const view = React.useContext(viewContext);

  switch (view) {
    case "horizontal-data-display":
      return <RowViewDeal {...props} />;
    case "horizontal-card-carousel":
      return <CarouselViewDeal {...props} />;
    default:
      return <DefaultDeal {...props} />;
  }
};

interface DealViewProp {
  a11yText: string;
  cardLink: string;
  data: string;
  label: string;
  modelKey: string;
}

const DefaultDeal: React.FC<DealViewProp> = ({ a11yText, cardLink, data, label, modelKey }) => (
  <UitkCardTextSegment
    className="cardTextContainer all-y-padding-four"
    separator={UitkCardSectionSeparatorType.TOP}
    key={modelKey}
  >
    <span className="dealText uitk-type-300" aria-hidden={true}>
      {label}
    </span>
    <span className="icon-right uitk-type-300 uitk-link" aria-hidden={true}>
      <TrackedLink className="uitk-type-300 uitk-link" moduleName="check-prices" rfrr={modelKey} href={cardLink}>
        {data}
      </TrackedLink>
    </span>

    <UitkCardLink>
      <TrackedLink className="uitk-card-link" moduleName="check-prices" rfrr={modelKey} href={cardLink}>
        {a11yText}
      </TrackedLink>
    </UitkCardLink>
  </UitkCardTextSegment>
);

const RowViewDeal: React.FC<DealViewProp> = ({ a11yText, cardLink, data, label, modelKey }) => (
  <UitkLayoutFlexItem grow={1}>
    <UitkLayoutGrid className="all-t-padding-three">
      <span className="uitk-type-300 uitk-type-bold all-y-padding-one" aria-hidden={true}>
        {label}
      </span>
      <span className="is-visually-hidden">{a11yText}</span>

      <TrackedLink className="uitk-type-300 uitk-link" moduleName="check-prices_row" rfrr={modelKey} href={cardLink}>
        {data}
      </TrackedLink>
    </UitkLayoutGrid>
  </UitkLayoutFlexItem>
);

const CarouselViewDeal: React.FC<DealViewProp> = ({ a11yText, cardLink, data, label, modelKey }) => (
  <UitkCardContent className="view-carousel-card">
    <UitkCard>
      <UitkCardContent>
        <UitkCardTextSegment key={modelKey}>
          <div className="uitk-type-center all-t-padding-one">
            <TrackedLink
              className="uitk-type-300 uitk-type-bold uitk-link"
              moduleName="check-prices_carouse"
              rfrr={modelKey}
              href={cardLink}
            >
              {data}
            </TrackedLink>
          </div>
          <div className="uitk-type-300 uitk-type-center all-t-padding-one" aria-hidden={true}>
            {label}
          </div>

          <span className="is-visually-hidden">{a11yText}</span>
        </UitkCardTextSegment>
      </UitkCardContent>
    </UitkCard>
  </UitkCardContent>
);

/**
 * This function handles creating the card link to HSR.
 * It is necessary because in the case of Today and Tomorrow, the back end module won't be returning the date as part of the URL
 * @param modelKey: to determine what deal we're dealing with.
 * @param backEndSuppliedLink: the url string built by the back-end module
 * @param context:
 */
const getCardLink = (modelKey: string, backEndSuppliedLink: string, context: ExtendedContextStore) => {
  const { formatDateString } = useLocalization();
  let cardLink = new HotelSearchLinkBuilder(backEndSuppliedLink).build(context); // may not contain dates for today or tomorrow (timezone differences)

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  if (modelKey === "today") {
    cardLink = new HotelSearchLinkBuilder(backEndSuppliedLink)
      .withStartDate(formatDateString(today, { raw: "yyyy-MM-dd" }))
      .withEndDate(formatDateString(tomorrow, { raw: "yyyy-MM-dd" }))
      .build(context);
  }
  if (modelKey === "tomorrow") {
    const afterTomorrow = new Date();
    afterTomorrow.setDate(tomorrow.getDate() + 1);
    cardLink = new HotelSearchLinkBuilder(backEndSuppliedLink)
      .withStartDate(formatDateString(tomorrow, { raw: "yyyy-MM-dd" }))
      .withEndDate(formatDateString(afterTomorrow, { raw: "yyyy-MM-dd" }))
      .build(context);
  }

  return cardLink;
};
